import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import {
  Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle,
} from '@/components/ui/drawer';
import { kelimeleriIceriyor } from '@/lib/arama';

// Günlük rapor formunda iş kalemi (poz) seçici.
//
// Ortak `SearchableSelect` etiketi tek satıra kırpıyordu; poz adları uzun ve
// birbirine çok benzer (ör. aynı iç ünite Multi Split ve Mini VRV grubunda,
// yalnız kapasite farklı), alt yüklenici doğru pozu seçtiğinden emin
// olamıyordu (kullanıcı, 2026-11-03). Burada hiçbir metin kırpılmaz:
// - seçim alttan açılan panelde, her poz kendi kartında: poz no, birim,
//   masraf grubu, TAM ad ve TAM açıklama
// - seçimden sonra kartta da tam ad + grup görünür, açıklama açılabilir.
// Saf props bileşeni — girişsiz form `lib/api.js`'i import etmeden kullanır.

const UZUN_ACIKLAMA = 140;

function Rozet({ children, koyu }) {
  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums
      ${koyu ? 'bg-[#2e2d29] text-white font-mono tracking-tight' : 'bg-[#efe8dc] text-[#5c554b]'}`}>
      {children}
    </span>
  );
}

function PozKarti({ k, secili, onSec }) {
  return (
    <button type="button" onClick={onSec} aria-pressed={secili} data-testid={`poz-secenek-${k.poz_no}`}
      className={`w-full text-left rounded-xl border bg-white p-3 transition-colors active:bg-[#f5f0e8]
        ${secili ? 'border-[#2e2d29] ring-2 ring-[#2e2d29]/15' : 'border-[#d0c4b0]'}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Rozet koyu>{k.poz_no}</Rozet>
            {k.birim && <Rozet>{k.birim}</Rozet>}
          </div>
          <p className="text-base leading-snug text-[#1a1916] font-medium break-words">{k.ad}</p>
          {k.grup && <p className="text-xs uppercase tracking-wide text-[#8a7e70]">{k.grup}</p>}
          {k.aciklama && <p className="text-sm leading-snug text-[#5c554b] break-words">{k.aciklama}</p>}
        </div>
        <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-full flex items-center justify-center
          ${secili ? 'bg-[#2e2d29] text-white' : 'border border-[#d0c4b0]'}`} aria-hidden="true">
          {secili && <Check size={14} />}
        </span>
      </div>
    </button>
  );
}

// kompakt: masaüstü tablo hücresi — küçük tetik, grup/açıklama yalnız ipucunda
export default function PozSecici({ kalemler = [], value, onChange, disabled, kayipMetni, sira, testId, kompakt = false }) {
  const [acik, setAcik] = useState(false);
  const [sorgu, setSorgu] = useState('');
  const [aciklamaAcik, setAciklamaAcik] = useState(false);

  const secili = kalemler.find((k) => k.id === value);
  const bulunan = useMemo(() => kalemler.filter((k) =>
    kelimeleriIceriyor(sorgu, `${k.poz_no} ${k.ad} ${k.grup || ''} ${k.aciklama || ''}`)), [kalemler, sorgu]);
  // Masraf grubuna göre başlıklar (ilk görülme sırası); tek grupsa başlık yok
  const gruplar = useMemo(() => {
    const sirali = [];
    bulunan.forEach((k) => {
      let g = sirali.find((x) => x.ad === (k.grup || ''));
      if (!g) { g = { ad: k.grup || '', ogeler: [] }; sirali.push(g); }
      g.ogeler.push(k);
    });
    return sirali;
  }, [bulunan]);

  const ac = () => { if (!disabled) { setSorgu(''); setAcik(true); } };
  const sec = (id) => { onChange(id); setAcik(false); setAciklamaAcik(false); };
  const uzun = (secili?.aciklama || '').length > UZUN_ACIKLAMA;

  return (
    <>
      <button type="button" onClick={ac} disabled={disabled} data-testid={testId} aria-haspopup="dialog"
        title={kompakt && secili ? [secili.ad, secili.grup, secili.aciklama].filter(Boolean).join('\n') : undefined}
        className={`w-full rounded-lg border bg-white text-left flex items-start gap-2
          disabled:opacity-60 active:bg-[#faf7f2] hover:bg-[#faf7f2] transition-colors
          ${kompakt ? 'min-h-10 px-2.5 py-2' : 'min-h-12 px-3 py-2.5'}
          ${kayipMetni ? 'border-[#b4442f]' : 'border-[#d0c4b0]'}`}>
        {secili && kompakt ? (
          <span className="min-w-0 flex-1 text-sm leading-snug text-[#1a1916] break-words">
            <span className="font-mono text-xs font-semibold text-[#5c554b] mr-1.5">{secili.poz_no}</span>
            {secili.ad}
          </span>
        ) : secili ? (
          <span className="min-w-0 flex-1 space-y-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <Rozet koyu>{secili.poz_no}</Rozet>
              {secili.birim && <Rozet>{secili.birim}</Rozet>}
            </span>
            <span className="block text-base leading-snug text-[#1a1916] font-medium break-words">{secili.ad}</span>
            {secili.grup && <span className="block text-xs uppercase tracking-wide text-[#8a7e70]">{secili.grup}</span>}
          </span>
        ) : (
          <span className={`min-w-0 flex-1 flex items-center gap-2 text-[#8a7e70] ${kompakt ? 'min-h-5 text-sm' : 'min-h-7 text-base'}`}>
            <Search size={kompakt ? 14 : 16} className="shrink-0" />
            <span className={kayipMetni ? 'text-[#b4442f]' : ''}>
              {kayipMetni ? `${kayipMetni} — artık kullanılmıyor, yenisini seçin` : 'Poz seçin'}
            </span>
          </span>
        )}
        <span className={`shrink-0 flex items-center gap-1 text-sm font-medium text-[#5c554b] ${kompakt ? 'min-h-5' : 'min-h-7'}`}>
          {secili && !kompakt ? 'Değiştir' : ''}<ChevronDown size={16} />
        </span>
      </button>

      {secili?.aciklama && !kompakt && (
        <div className="mt-1.5">
          <p className={`text-sm leading-snug text-[#5c554b] break-words ${uzun && !aciklamaAcik ? 'line-clamp-3' : ''}`}>
            {secili.aciklama}
          </p>
          {uzun && (
            <button type="button" onClick={() => setAciklamaAcik((a) => !a)}
              className="mt-0.5 min-h-11 text-sm font-medium text-[#2e2d29] underline underline-offset-2">
              {aciklamaAcik ? 'Daha az göster' : 'Açıklamanın tamamı'}
            </button>
          )}
        </div>
      )}

      <Drawer open={acik} onOpenChange={setAcik} shouldScaleBackground={false}>
        <DrawerContent className="mx-auto w-full max-w-lg lg:max-w-3xl h-[90dvh] bg-[#faf7f2] border-[#d0c4b0]"
          data-testid={`poz-paneli-${sira || ''}`}>
          <DrawerHeader className="text-left pb-2">
            <DrawerTitle className="text-[#1a1916]">{sira ? `${sira}. iş — ` : ''}İş kalemi seçin</DrawerTitle>
            <DrawerDescription className="text-[#5c554b]">
              Size tanımlı {kalemler.length} poz. Ad ve açıklamaya bakarak doğru pozu seçin.
            </DrawerDescription>
            <div className="relative mt-2">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
              <input type="text" value={sorgu} onChange={(e) => setSorgu(e.target.value)} enterKeyHint="search"
                placeholder="Poz no, iş adı ya da açıklama…" aria-label="Poz ara" data-testid="poz-ara"
                className="w-full h-12 rounded-lg border border-[#d0c4b0] bg-white pl-10 pr-11 text-base text-[#1a1916]
                  placeholder:text-[#8a7e70] focus:outline-none focus:ring-2 focus:ring-[#2e2d29]/25" />
              {sorgu && (
                <button type="button" onClick={() => setSorgu('')} aria-label="Aramayı temizle"
                  className="absolute right-0.5 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center text-[#8a7e70]">
                  <X size={18} />
                </button>
              )}
            </div>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-4">
            {gruplar.length === 0 && (
              <p className="py-8 text-center text-sm text-[#5c554b]">
                Bu iş size tanımlı değil — formda <b>İlave İş</b> olarak ekleyin.
              </p>
            )}
            {gruplar.map((g) => (
              <section key={g.ad || '_'} className="space-y-2">
                {gruplar.length > 1 && g.ad && (
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[#8a7e70] pt-1">{g.ad}</h3>
                )}
                {g.ogeler.map((k) => (
                  <PozKarti key={k.id} k={k} secili={k.id === value} onSec={() => sec(k.id)} />
                ))}
              </section>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
